import { cloneDeep } from "lodash";
import { replace } from "redux-first-history";
import { call, put, race, select, take, takeLatest, takeLeading, type SagaReturnType } from "redux-saga/effects";

import { selectCampaignById } from "@ds/modules/content/campaigns/redux/selectors";
import { campaignsActions } from "@ds/modules/content/campaigns/redux/slice";
import {
  redistributeAfterRemoval,
  redistributeRemainingWeights,
  redistributeWeights,
} from "@ds/modules/content/campaigns/utils/helpers";
import { iotEventCampaignItemUpdate } from "@ds/modules/iot/redux/actions";
import { toastShowErrorAction, toastShowSuccessAction } from "@ds/modules/notifications/redux/actions";
import { selectTableData } from "@ds/modules/table-data/redux/selectors";
import { convertToApiQueryInfo } from "@ds/modules/table-data/utils/common";
import { DetailsTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { UnexpectedError } from "@ds/utils/errors";
import { logger } from "@ds/utils/logger";
import { UNLIMITED_PAGINATION } from "@ds/utils/query";
import { takeLatestOrEvery, takeLeadingOrEvery } from "@ds/utils/saga-helpers";
import { isErrorLike } from "@ds/utils/type-guards/error-guards";

import { isApiCampaignItemQueryOutput } from "../utils/model";
import { normalize } from "../utils/normalizer";
import { selectCampaignItemById, selectCampaignItemsByCampaignId, selectTableCampaignItems } from "./selectors";
import { campaignItemsActions } from "./slice";
import { campaignItemsService } from "../utils/api";

function* fetchCampaignItems({ meta }: ReturnType<typeof campaignItemsActions.fetchCampaignItems>) {
  try {
    const { sorting, queryInfo, isFetchedAlready } = selectTableData(
      yield select(),
      meta.options.tableType || DetailsTableDataTypeEnum.ContentCampaignItems,
      meta.filters?.campaignId?.toString(),
      meta.filters,
    );

    const newMeta = cloneDeep(meta);
    const resultFromCache: QueryOutput<CampaignItem> = { items: [] };

    if (!newMeta.options.cache?.disableCache) {
      if (isFetchedAlready && newMeta.options.tableType) {
        resultFromCache.items = selectTableCampaignItems(yield select(), newMeta.options.tableType);
        newMeta.options.cache = {
          ...newMeta.options.cache,
          fetchedFromCache: true,
        };
      } else if (newMeta.filters?.campaignId && Object.keys(newMeta.filters).length === 1) {
        const campaign = selectCampaignById(yield select(), newMeta.filters.campaignId);
        resultFromCache.items = selectCampaignItemsByCampaignId(yield select(), [newMeta.filters.campaignId]);
        if (resultFromCache.items.length === campaign?.campaign_items_count) {
          newMeta.options.cache = {
            ...newMeta.options.cache,
            fetchedFromCache: true,
          };
        }
      }
    }

    let result: QueryOutput<CampaignItem> | QueryOutput<ApiCampaignItem> = newMeta.options.cache?.fetchedFromCache
      ? resultFromCache
      : yield call(
          [campaignItemsService, campaignItemsService.getCampaignItems],
          convertToApiQueryInfo(sorting, queryInfo),
          UNLIMITED_PAGINATION,
        );

    result = isApiCampaignItemQueryOutput(result) ? normalize(result) : result;
    result = newMeta.options.tableType ? result : { items: result.items };

    yield put(campaignItemsActions.fetchCampaignItemsSucceeded(result, newMeta));

    if (!newMeta.options.cache?.fetchedFromCache && newMeta.filters?.campaignId) {
      yield put(
        campaignsActions.updateCampaignItemsCount({
          campaignId: newMeta.filters.campaignId,
          count: result.items.length,
        }),
      );
    }
  } catch (err) {
    const errorTitle = "Fetch campaign items";
    if (isErrorLike(err)) {
      yield put(campaignItemsActions.fetchCampaignItemsFailed(meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* createCampaignItem({ payload, meta }: ReturnType<typeof campaignItemsActions.createCampaignItem>) {
  try {
    const campaign = selectCampaignById(yield select(), meta.filters?.campaignId || 0);
    if (!campaign) {
      yield put(toastShowErrorAction("Campaign not found", "Add campaign item"));
      return;
    }

    // Capture existing items before creating so we can redistribute their weights
    const existingItems: CampaignItem[] = selectCampaignItemsByCampaignId(yield select(), [campaign.id]);

    const result: SagaReturnType<typeof campaignItemsService.createCampaignItem> = yield call(
      [campaignItemsService, campaignItemsService.createCampaignItem],
      { ...payload, campaign_id: campaign.id, campaign_order: -1 },
    );

    // Redistribute existing items proportionally given the new item's weight
    if (existingItems.length > 0 && payload.weight !== undefined) {
      const redistributed = redistributeRemainingWeights(existingItems, payload.weight);
      for (const item of redistributed) {
        const orig = existingItems.find(e => e.id === item.id);
        if (orig && orig.weight !== item.weight) {
          yield call([campaignItemsService, campaignItemsService.updateCampaignItems], [item.id], {
            weight: item.weight,
          });
        }
      }
    }

    yield put(campaignItemsActions.createCampaignItemSucceeded(normalize(result), meta));
    yield put(toastShowSuccessAction("Campaign item was added successfully"));
    yield put(campaignItemsActions.selectCampaignItems([result]));

    yield put(
      campaignItemsActions.fetchCampaignItems(meta.filters, {
        tableType: DetailsTableDataTypeEnum.ContentCampaignItems,
      }),
    );

    yield put(campaignsActions.fetchCampaign(campaign.id, { cache: { disableCache: true } }));
  } catch (err) {
    const errorTitle = "Add campaign item";
    if (isErrorLike(err)) {
      yield put(campaignItemsActions.createCampaignItemFailed(payload, meta, err));
      if ((err as ApiError).status === 409) {
        yield put(
          toastShowErrorAction(
            "This playlist is already in this campaign. Each playlist may only appear once.",
            errorTitle,
          ),
        );
      } else {
        yield put(toastShowErrorAction(err, errorTitle));
      }
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* updateCampaignItems({ payload }: ReturnType<typeof campaignItemsActions.updateCampaignItems>) {
  try {
    const campaignId: number | undefined = payload.ids[0]
      ? selectCampaignItemById(yield select(), payload.ids[0])?.campaign_id
      : undefined;

    const result: SagaReturnType<typeof campaignItemsService.updateCampaignItems> = yield call(
      [campaignItemsService, campaignItemsService.updateCampaignItems],
      payload.ids,
      payload.data,
    );

    const allResults: ApiCampaignItem[] = [...normalize(result)];

    // Redistribute sibling weights when a single item's weight is updated
    if (payload.ids.length === 1 && payload.data.weight !== undefined) {
      const editedId = payload.ids[0];
      const editedItem: CampaignItem = selectCampaignItemById(yield select(), editedId);
      if (editedItem) {
        const allItems: CampaignItem[] = selectCampaignItemsByCampaignId(yield select(), [editedItem.campaign_id]);
        const redistributed = redistributeWeights(allItems, editedId, payload.data.weight as number);
        for (const item of redistributed) {
          const orig = allItems.find(e => e.id === item.id);
          if (item.id !== editedId && orig && orig.weight !== item.weight) {
            const sibResult: SagaReturnType<typeof campaignItemsService.updateCampaignItems> = yield call(
              [campaignItemsService, campaignItemsService.updateCampaignItems],
              [item.id],
              { weight: item.weight },
            );
            allResults.push(...normalize(sibResult));
          }
        }
      }
    }

    yield put(campaignItemsActions.updateCampaignItemsSucceeded(allResults));
    yield put(toastShowSuccessAction("Campaign item(s) were updated successfully"));

    if (campaignId) {
      yield put(
        campaignItemsActions.fetchCampaignItems(
          { campaignId },
          { tableType: DetailsTableDataTypeEnum.ContentCampaignItems, cache: { disableCache: true } },
        ),
      );
    }
  } catch (err) {
    const errorTitle = "Update campaign item(s)";
    if (isErrorLike(err)) {
      yield put(campaignItemsActions.updateCampaignItemsFailed(payload, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* deleteCampaignItems({ payload, meta }: ReturnType<typeof campaignItemsActions.deleteCampaignItems>) {
  try {
    // Capture all items before deleting so we can redistribute weights afterward
    const allItems: CampaignItem[] = meta.filters?.campaignId
      ? selectCampaignItemsByCampaignId(yield select(), [meta.filters.campaignId])
      : [];

    if (payload.length === 1) {
      yield call([campaignItemsService, campaignItemsService.deleteCampaignItem], payload[0]);
    } else {
      yield call([campaignItemsService, campaignItemsService.deleteCampaignItems], payload);
    }

    // Redistribute weights across remaining items
    if (allItems.length > 0) {
      const redistributed = redistributeAfterRemoval(allItems, payload);
      for (const item of redistributed) {
        const orig = allItems.find(e => e.id === item.id);
        if (orig && orig.weight !== item.weight) {
          yield call([campaignItemsService, campaignItemsService.updateCampaignItems], [item.id], {
            weight: item.weight,
          });
        }
      }
    }

    yield put(campaignItemsActions.deleteCampaignItemsSucceeded(payload, meta));
    yield put(toastShowSuccessAction("Campaign item(s) were deleted successfully"));

    if (meta.options?.redirectTo) {
      yield put(replace(meta.options.redirectTo));
    }

    if (meta.filters?.campaignId) {
      yield put(campaignsActions.fetchCampaign(meta.filters.campaignId, { cache: { disableCache: true } }));
      yield put(campaignItemsActions.fetchCampaignItems(meta.filters));
    }
  } catch (err) {
    const errorTitle = "Delete campaign item(s)";
    if (isErrorLike(err)) {
      yield put(campaignItemsActions.deleteCampaignItemsFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* reorderCampaignItem({ payload, meta }: ReturnType<typeof campaignItemsActions.reorderCampaignItems>) {
  try {
    const campaignId = meta.filters?.campaignId;
    if (!campaignId) {
      return;
    }

    const entities: CampaignItem[] = selectTableCampaignItems(yield select());
    const reordered = [...entities];
    const [moved] = reordered.splice(payload.currIndex, 1);
    reordered.splice(payload.newIndex, 0, moved);
    const orderedIds = reordered.map(item => item.id);

    yield call([campaignItemsService, campaignItemsService.reorderCampaignItems], campaignId, orderedIds);

    yield put(campaignItemsActions.reorderCampaignItemsSucceeded(payload, meta));
    yield put(toastShowSuccessAction("Campaign item was reordered successfully"));

    yield put(
      campaignItemsActions.fetchCampaignItems(
        { campaignId },
        { tableType: DetailsTableDataTypeEnum.ContentCampaignItems, cache: { disableCache: true } },
      ),
    );
  } catch (err) {
    const errorTitle = "Reorder campaign item";
    if (isErrorLike(err)) {
      yield put(campaignItemsActions.reorderCampaignItemsFailed(payload, meta, err));
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* batchUpdateCampaignItemWeights({
  payload,
}: ReturnType<typeof campaignItemsActions.batchUpdateCampaignItemWeights>) {
  try {
    const campaignId: number | undefined = payload[0]?.id
      ? selectCampaignItemById(yield select(), payload[0].id)?.campaign_id
      : undefined;

    const allResults: ApiCampaignItem[] = [];
    for (const { id, fields } of payload) {
      const res: SagaReturnType<typeof campaignItemsService.updateCampaignItems> = yield call(
        [campaignItemsService, campaignItemsService.updateCampaignItems],
        [id],
        fields,
      );
      allResults.push(...normalize(res));
    }
    yield put(campaignItemsActions.updateCampaignItemsSucceeded(allResults));
    yield put(toastShowSuccessAction("Campaign item(s) were updated successfully"));

    if (campaignId) {
      yield put(
        campaignItemsActions.fetchCampaignItems(
          { campaignId },
          { tableType: DetailsTableDataTypeEnum.ContentCampaignItems, cache: { disableCache: true } },
        ),
      );
    }
  } catch (err) {
    const errorTitle = "Update campaign item(s)";
    if (isErrorLike(err)) {
      yield put(toastShowErrorAction(err, errorTitle));
    } else {
      logger.error(`${errorTitle}: ${UnexpectedError}`);
      yield put(toastShowErrorAction(UnexpectedError, errorTitle));
    }
  }
}

function* refetchCampaignItems({ payload }: ReturnType<typeof iotEventCampaignItemUpdate>) {
  yield put(campaignItemsActions.iotUpdate(payload));
}

export function* watchCampaignItems() {
  yield takeLatestOrEvery(campaignItemsActions.fetchCampaignItems, function* (action) {
    yield race({
      task: call(fetchCampaignItems, action),
      cancel: take([campaignItemsActions.selectCampaignItems, campaignItemsActions.toggleSelected]),
    });
  });

  yield takeLeadingOrEvery(campaignItemsActions.createCampaignItem, createCampaignItem);
  yield takeLeading(campaignItemsActions.updateCampaignItems, updateCampaignItems);
  yield takeLeading(campaignItemsActions.batchUpdateCampaignItemWeights, batchUpdateCampaignItemWeights);
  yield takeLeading(campaignItemsActions.deleteCampaignItems, deleteCampaignItems);
  yield takeLeading(campaignItemsActions.reorderCampaignItems, reorderCampaignItem);
  yield takeLatest(iotEventCampaignItemUpdate, refetchCampaignItems);
}
